import {
  CircularProgress,
  Typography,
  Grid,
  Autocomplete,
  TextField
} from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { httpRequest } from 'src/services/httpRequest';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HeightIcon from '@mui/icons-material/Height';
import ScaleIcon from '@mui/icons-material/Scale';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import InfoIcon from '@mui/icons-material/Info';
import { format } from 'date-fns';
import type { SvgIconComponent } from '@mui/icons-material';

const PET_DROPDOWN_SHELL_SX = {
  width: '100%',
  minWidth: 0,
  maxWidth: '100%',
  mb: 3,
  mt: 1
};

type PetDetailRowProps = {
  icon: SvgIconComponent;
  label: string;
  value?: string | number | null;
};

const PetDetailRow = ({ icon: Icon, label, value }: PetDetailRowProps) => (
  <Grid item xs={12} sm={6} lg={12}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        mb: 1.5,
        minWidth: 0
      }}
    >
      <Icon
        sx={{
          color: 'primary.main',
          fontSize: 20,
          mt: 0.25,
          flexShrink: 0
        }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.3 }}
        >
          {label}
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontWeight: 500, wordBreak: 'break-word' }}
        >
          {value !== undefined && value !== null && value !== '' ? value : '—'}
        </Typography>
      </Box>
    </Box>
  </Grid>
);

const PetSelection = ({
  petDetails,
  setPetDetails,
  editData = null,
  petListVersion = 0
}) => {
  const [loading, setLoading] = useState(false);
  const [pets, setPets] = useState([]);
  const dispatch = useDispatch();

  const fetchPetsData = async () => {
    setLoading(true);
    try {
      const data = await httpRequest('GET', '/pet/petDetails/all');
      if (data?.status === 'success') {
        const items = data?.data || [];
        const typeOrder = (pet) => {
          const t = (pet?.PetType || '').toLowerCase();
          if (t === 'dog') return 0;
          if (t === 'cat') return 1;
          return 2;
        };
        const sortedPets = [...items].sort((a, b) => {
          const orderA = typeOrder(a);
          const orderB = typeOrder(b);
          if (orderA !== orderB) return orderA - orderB;
          const nameA = (a?.PetName || '').toString().trim().toLowerCase();
          const nameB = (b?.PetName || '').toString().trim().toLowerCase();
          if (nameA && nameB) return nameA.localeCompare(nameB);
          if (!nameA && nameB) return 1;
          if (nameA && !nameB) return -1;
          return 0;
        });
        setPets(sortedPets);
        if (editData?.PetID) {
          const currentPet =
            sortedPets.find((pet) => String(pet?.PetID) === String(editData.PetID)) ||
            sortedPets[0] ||
            null;
          setPetDetails(currentPet);
        }
      } else if (data?.type === 'NETWORK_ERROR') {
        toast.error('Network error occurred');
      } else {
        toast.error(data?.message);
        dispatch(handleError(data?.message));
      }
    } catch {
      toast.error('An error occurred while fetching pets');
      dispatch(handleError('An error occurred while fetching pets'));
    } finally {
      setLoading(false);
    }
  };

  const getOptionLabel = (pet) => {
    if (!pet) return '';
    const petName = (pet?.PetName || '').toString().trim();
    const profileOwner = (pet?.OwnerName || '').toString().trim();
    if (profileOwner) {
      return petName ? `${petName} - ${profileOwner}` : profileOwner;
    }
    const o = pet?.owner;
    const accountOwner = o
      ? [o.FirstName, o.LastName].filter(Boolean).join(' ').trim()
      : '';
    if (accountOwner && petName) return `${petName} - ${accountOwner}`;
    return petName || accountOwner || '';
  };

  useEffect(() => {
    fetchPetsData();
  }, [petListVersion]);

  const getDob = () => {
    if (!petDetails?.PetDOB) return '';
    const petDOB = new Date(petDetails.PetDOB);
    return !isNaN(petDOB.getTime()) ? format(petDOB, 'MM/dd/yyyy') : '';
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0, alignSelf: 'stretch' }}>
      <Typography variant="h5" component="div" gutterBottom>
        {editData ? 'Pet Details' : 'Pet Selection'}
      </Typography>

      {loading ? (
        <Box
          sx={{
            ...PET_DROPDOWN_SHELL_SX,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 56
          }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          {!editData && (
            <Autocomplete
              fullWidth
              sx={PET_DROPDOWN_SHELL_SX}
              options={pets}
              value={petDetails}
              onChange={(_, newValue) => setPetDetails(newValue ?? null)}
              getOptionLabel={getOptionLabel}
              getOptionKey={(pet) => pet?.PetID}
              isOptionEqualToValue={(option, value) =>
                String(option?.PetID) === String(value?.PetID)
              }
              groupBy={(pet) => {
                const t = (pet?.PetType || '').toLowerCase();
                if (t === 'dog') return 'Dogs';
                if (t === 'cat') return 'Cats';
                return 'Other';
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Pet"
                  placeholder={
                    pets?.length === 0
                      ? 'No pets found. Add a pet in My Pets first.'
                      : 'Select a pet...'
                  }
                />
              )}
            />
          )}

          {petDetails && (
            <Box sx={{ mb: 3, mt: 1 }}>
              <Grid container spacing={1.5}>
                <PetDetailRow
                  icon={InfoIcon}
                  label="Type"
                  value={petDetails?.PetType}
                />
                <PetDetailRow
                  icon={InfoIcon}
                  label="Name"
                  value={petDetails?.PetName}
                />
                <PetDetailRow
                  icon={CalendarTodayIcon}
                  label="Date Of Birth"
                  value={getDob()}
                />
                <PetDetailRow
                  icon={HeightIcon}
                  label="Breed"
                  value={petDetails?.PetBreed}
                />
                <PetDetailRow
                  icon={ScaleIcon}
                  label="Weight"
                  value={
                    petDetails?.PetWeight
                      ? `${petDetails.PetWeight} Lbs`
                      : undefined
                  }
                />
                <PetDetailRow
                  icon={ScaleIcon}
                  label="Pet Hair"
                  value={petDetails?.PetHair}
                />
                <PetDetailRow
                  icon={ColorLensIcon}
                  label="Color"
                  value={petDetails?.PetColor}
                />
                <PetDetailRow
                  icon={ColorLensIcon}
                  label="Pet Sex"
                  value={petDetails?.PetSex}
                />
                <PetDetailRow
                  icon={ColorLensIcon}
                  label="Spayed Neutered"
                  value={petDetails?.IsSpayedNeutered ? 'Yes' : 'No'}
                />
                <PetDetailRow
                  icon={ColorLensIcon}
                  label="Chip Number"
                  value={petDetails?.ChipNumber}
                />
              </Grid>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PetSelection;
